/**
 * Weapon runtime state — one instance per weapon slot the player owns.
 * The static balance numbers live in `CONFIG.weapons` and `CONFIG.sound.loudness`;
 * this file wraps them with per-instance ammo + cooldown.
 */

import { CONFIG } from '../config';

export type WeaponKind = 'smg' | 'silencedPistol' | 'knife' | 'unarmed' | 'heal' | 'pipe';

export interface Weapon {
  kind: WeaponKind;
  name: string;
  damage: number;
  rangeTiles: number;
  fireIntervalSec: number;
  /** Max ammo. Undefined for melee. */
  magazine?: number;
  /** Current ammo. Undefined for melee (infinite). */
  ammo?: number;
  /** Sound event loudness on discharge. */
  loudness: number;
  isMelee: boolean;
  /** Seconds until the weapon can fire again; 0 = ready. */
  cooldown: number;
  /** If true, ammo checks + decrement are skipped. Used for guard weapons. */
  unlimitedAmmo?: boolean;
  /** Needs BOTH arms to fire (e.g. the SMG). A single crippled arm disables it;
   *  one-handed weapons (pistol/knife/fists) still work on one good arm. See
   *  `canOperateWeapon`. */
  twoHanded?: boolean;
  /** A heavy blunt melee (pipe/sledge) — the ONLY melee that can smash a lock.
   *  The knife/fists cannot. */
  heavyBlunt?: boolean;
  /** Whether firing produces a muzzle flash (light + white flash). An ITEM
   *  aspect: silenced weapons have none regardless of which slot they're in. */
  muzzleFlash: boolean;
}

export function createSmg(): Weapon {
  const w = CONFIG.weapons.smg;
  return {
    kind: 'smg',
    name: 'SMG',
    damage: w.damage,
    rangeTiles: w.rangeTiles,
    fireIntervalSec: w.fireIntervalSec,
    magazine: w.magazine,
    ammo: w.magazine,
    loudness: CONFIG.sound.loudness.smgShot,
    isMelee: false,
    cooldown: 0,
    muzzleFlash: true,
    twoHanded: true, // needs both arms — a crippled arm jams it
  };
}

export function createSilencedPistol(): Weapon {
  const w = CONFIG.weapons.silencedPistol;
  return {
    kind: 'silencedPistol',
    name: 'P-Sil',
    damage: w.damage,
    rangeTiles: w.rangeTiles,
    fireIntervalSec: w.fireIntervalSec,
    magazine: w.magazine,
    ammo: w.magazine,
    loudness: CONFIG.sound.loudness.silencedPistol,
    isMelee: false,
    cooldown: 0,
    muzzleFlash: false, // silenced → no muzzle flash (item aspect)
  };
}

export function createKnife(): Weapon {
  return {
    kind: 'knife',
    name: 'Knife',
    damage: CONFIG.weapons.knife.damage,
    rangeTiles: 1,
    fireIntervalSec: 0.4,
    // Melee = no magazine tracking.
    loudness: CONFIG.sound.loudness.knifeTakedown,
    isMelee: true,
    cooldown: 0,
    muzzleFlash: false,
  };
}

/** Heavy blunt melee (pipe/sledge) — loud, slow, but the only melee that can
 *  smash a lock off a door or container. Not slotted into the loadout yet. */
export function createPipe(): Weapon {
  const w = CONFIG.weapons.heavyBlunt;
  return {
    kind: 'pipe',
    name: 'Pipe',
    damage: w.damage,
    rangeTiles: 1,
    fireIntervalSec: w.fireIntervalSec,
    loudness: CONFIG.sound.loudness.knifeTakedown,
    isMelee: true,
    heavyBlunt: true,
    cooldown: 0,
    muzzleFlash: false,
  };
}

/**
 * Guard sidearm — silenced pistol with unlimited ammo so patrols don't
 * silently run out during a firefight. Same balance as the player's
 * silenced pistol otherwise.
 */
export function createGuardPistol(): Weapon {
  const w = createSilencedPistol();
  w.name = 'G-Pistol';
  w.unlimitedAmmo = true;
  if (w.magazine !== undefined) w.ammo = w.magazine;
  return w;
}

/**
 * Field medkit — occupies a weapon slot but heals the player when "fired"
 * instead of doing damage. Limited charges; each use restores a chunk of
 * HP across every limb (see main.ts fire path).
 */
export function createHeal(): Weapon {
  return {
    kind: 'heal',
    name: 'Medkit',
    damage: 0,
    rangeTiles: 0,
    fireIntervalSec: 0.8,
    magazine: 3,
    ammo: 3,
    loudness: 0,
    isMelee: true, // gates it out of the ranged bullet path
    cooldown: 0,
    muzzleFlash: false,
  };
}

/**
 * Unarmed. Also instant-takedowns on unaware targets (see combat.ts) but is
 * louder than the knife — a punch/grapple is not a precise weapon.
 */
export function createUnarmed(): Weapon {
  return {
    kind: 'unarmed',
    name: 'Fists',
    damage: CONFIG.weapons.unarmed.damage,
    rangeTiles: 1,
    fireIntervalSec: CONFIG.weapons.unarmed.fireIntervalSec,
    loudness: CONFIG.sound.loudness.punch,
    isMelee: true,
    cooldown: 0,
    muzzleFlash: false,
  };
}

/** Refill ammo across every weapon that has a magazine. */
export function refillAmmo(weapons: readonly Weapon[]): void {
  for (const w of weapons) {
    if (w.magazine !== undefined) w.ammo = w.magazine;
  }
}

/** True if the weapon is ready to fire (cooldown 0, ammo available). */
export function canFire(w: Weapon): boolean {
  if (w.cooldown > 0) return false;
  if (!w.isMelee && (w.ammo ?? 0) <= 0) return false;
  return true;
}

/** Advance every weapon's cooldown by `dt`. Call once per tick. */
export function tickWeaponCooldowns(weapons: readonly Weapon[], dt: number): void {
  for (const w of weapons) if (w.cooldown > 0) w.cooldown = Math.max(0, w.cooldown - dt);
}
